/**
 * PreferencesPage.tsx — /preferences, where someone changes their marketing
 * consent from the link at the bottom of any of our marketing emails.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: marketing consent (2026-09-11).
 *
 * WHY IT IS PUBLIC. UK GDPR Art 7(3): withdrawing consent must be as easy as
 * giving it. A person who ticked the box on the questionnaire's consent step
 * (the Result screen, before 2026-09-23) may never have made an account, and a
 * sign-in wall in front of "stop emailing me" fails that test.
 * So the capability is the signed link token clinic-service puts in the email,
 * and all it can do is read and set this one person's two consent booleans.
 *
 * THE TOKEN COMES OUT OF THE FRAGMENT AND IS SCRUBBED IMMEDIATELY —
 * `#t=<token>`, never a query string, the same discipline as /resubscribe and
 * the pharmacy sign-in (`lib/hashToken`). A query string is written into
 * LB/WAF/nginx access logs on the first page load; a fragment never leaves the
 * browser, and scrubbing it keeps it out of history, screen-shares and a copied
 * address. Every request carries it in the BODY.
 *
 * ONE FAILURE STATE FOR A LINK THE SERVER WILL NOT HONOUR. A 404 (unknown,
 * forged, or a link for someone whose record is gone) is one calm "this link
 * isn't working" with the other way to reach us, never a guess at which case it
 * was. A transport failure is different — it says nothing about the link — so
 * it gets a Retry instead.
 *
 * NO TRACKING. `/preferences` is in the tracking gate's `RESERVED_SEGMENTS`
 * (`tracking/gtm.ts`); a container on the page where a person withdraws
 * marketing consent would be absurd as well as a leak.
 *
 * A11y: one `<h1>` (the wording's own heading) naming the consent group; a
 * document title of its own; the saved outcome in a `role="status"` region that
 * takes focus so it is announced and so keyboard users land beside it; a save
 * failure in a `role="alert"` the Save button is described by.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { saveMarketingPreferences, viewMarketingPreferences } from '../api/journey';
import type { MarketingChannel, MarketingConsentState } from '../api/types';
import { readHashToken, scrubHashFromAddressBar } from '../lib/hashToken';
import { Alert, Button, ErrorState, PageFrame, Spinner, Title } from '../ui/kit';
import { MARKETING_CONSENT_WORDING, MARKETING_CONSENT_WORDING_VERSION } from './consentWording';
import { MarketingConsentFields, UNTICKED, type MarketingChoices } from './MarketingConsentFields';

/** The browser tab's name while this page is open. */
export const PREFERENCES_PAGE_TITLE = 'Keeping in touch — The GP Service';

const SAVE_FAILED = "Sorry — we couldn't save your choices just now. Please try again.";

type Stage =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'unavailable' }
  | { kind: 'ready'; stored: MarketingConsentState };

/** A UK mobile on file, or a WhatsApp grant already recorded (so it can always be withdrawn). */
function whatsappShown(stored: MarketingConsentState): boolean {
  return stored.whatsappAvailable || stored.whatsapp;
}

/** The server said "no such link". Anything else is a transport problem, not a verdict on the link. */
function isUnknownLink(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * The saved message, from the server's ANSWER rather than the request: asking for
 * WhatsApp without a UK mobile on file is recorded as not granted, and a page
 * that said "saved" over a box that silently did not take would be telling the
 * person something untrue about their own choice.
 */
export function savedMessage(requested: MarketingChoices, after: MarketingConsentState): string {
  if (requested.whatsapp && !after.whatsapp) {
    return "Your choices have been saved. We couldn't turn on WhatsApp messages because we don't have a UK mobile number for you — a member of our team can help with that.";
  }
  return 'Your choices have been saved.';
}

export function PreferencesPage() {
  // Read the fragment during the FIRST render: the scrub below removes it.
  const [token] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : readHashToken(window.location.hash),
  );
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [choices, setChoices] = useState<MarketingChoices>(UNTICKED);
  const [loadNonce, setLoadNonce] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // An object, not a string: saving the same thing twice must move focus twice.
  const [saved, setSaved] = useState<{ message: string } | null>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const errorId = useId();

  // Scrub the live credential out of the address bar the moment we hold it.
  useEffect(() => {
    if (token) scrubHashFromAddressBar();
  }, [token]);

  // A page of its own deserves a tab title of its own (WCAG 2.4.2).
  useEffect(() => {
    const previous = document.title;
    document.title = PREFERENCES_PAGE_TITLE;
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setStage({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    setStage({ kind: 'loading' });
    viewMarketingPreferences(token).then(
      (state) => {
        if (cancelled) return;
        setChoices({ email: state.email, whatsapp: state.whatsapp });
        setStage({ kind: 'ready', stored: state });
      },
      (error: unknown) => {
        if (cancelled) return;
        setStage(isUnknownLink(error) ? { kind: 'invalid' } : { kind: 'unavailable' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [token, loadNonce]);

  // Announce the outcome and put the keyboard beside it.
  useEffect(() => {
    if (saved) statusRef.current?.focus();
  }, [saved]);

  function toggle(channel: MarketingChannel) {
    // A new change makes "saved" stale — it is no longer true of what is on screen.
    setSaved(null);
    setChoices((current) => ({ ...current, [channel]: !current[channel] }));
  }

  function save(event: FormEvent) {
    event.preventDefault();
    if (!token || saving || stage.kind !== 'ready') return;
    // A hidden WhatsApp box cannot have been changed, so it carries what is stored.
    const requested: MarketingChoices = {
      email: choices.email,
      whatsapp: whatsappShown(stage.stored) ? choices.whatsapp : stage.stored.whatsapp,
    };
    setSaving(true);
    setSaveError(null);
    setSaved(null);
    saveMarketingPreferences({
      token,
      email: requested.email,
      whatsapp: requested.whatsapp,
      wordingVersion: MARKETING_CONSENT_WORDING_VERSION,
    }).then(
      (after) => {
        setSaving(false);
        setChoices({ email: after.email, whatsapp: after.whatsapp });
        setStage({ kind: 'ready', stored: after });
        setSaved({ message: savedMessage(requested, after) });
      },
      (error: unknown) => {
        setSaving(false);
        if (isUnknownLink(error)) {
          setStage({ kind: 'invalid' });
          return;
        }
        setSaveError(SAVE_FAILED);
      },
    );
  }

  return (
    <PageFrame narrow>
      <Title id={headingId}>{MARKETING_CONSENT_WORDING.heading}</Title>

      {stage.kind === 'loading' ? <Spinner label="Loading your choices…" /> : null}

      {stage.kind === 'invalid' ? (
        <div role="status">
          <h2 style={{ fontSize: '19px', margin: '10px 0 6px', color: 'var(--gps-text)' }}>{"This link isn't working"}</h2>
          <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.5, color: 'var(--gps-muted)' }}>
            You can still change how we keep in touch — just ask a member of our team and they will do it for you.
          </p>
        </div>
      ) : null}

      {stage.kind === 'unavailable' ? (
        <ErrorState message="We couldn't load your choices just now." onRetry={() => setLoadNonce((n) => n + 1)} />
      ) : null}

      {stage.kind === 'ready' ? (
        <form onSubmit={save} noValidate>
          <MarketingConsentFields
            labelledBy={headingId}
            choices={choices}
            showWhatsapp={whatsappShown(stage.stored)}
            disabled={saving}
            onToggle={toggle}
          />
          {saveError ? (
            <Alert tone="danger" role="alert" id={errorId} style={{ marginTop: 'var(--gps-s-3)' }}>
              {saveError}
            </Alert>
          ) : null}
          <div style={{ marginTop: 'var(--gps-s-4)' }}>
            <Button type="submit" variant="primary" disabled={saving} aria-describedby={saveError ? errorId : undefined}>
              {saving ? 'Saving…' : 'Save my choices'}
            </Button>
          </div>
          <div ref={statusRef} tabIndex={-1} role="status" style={{ marginTop: 'var(--gps-s-3)' }}>
            {saved ? <Alert tone="success">{saved.message}</Alert> : null}
          </div>
        </form>
      ) : null}
    </PageFrame>
  );
}
